// The backoffice API client — the dashboard's client shape over the admin
// contract. Same single token, single refresher, single in-flight refresh
// guard; the reasoning for each piece lives in the dashboard's copy and holds
// unchanged here (copied per the recorded no-shared-ui-package decision).
import { initClient, tsRestFetchApi, type ApiFetcherArgs } from "@ts-rest/core";
import { adminContract } from "@gb/admin-contracts";
import { isAuthCall } from "@gb/contracts";

// VITE_* vars are inlined at build time by Vite.
export const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:3000";

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

type Refresher = () => Promise<string | null>;

let refresher: Refresher | null = null;

export function setTokenRefresher(fn: Refresher | null): void {
  refresher = fn;
}

let inFlight: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  if (!refresher) return Promise.resolve(null);
  inFlight ??= refresher().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

const withToken = (args: ApiFetcherArgs, token: string | null): ApiFetcherArgs =>
  token
    ? {
        ...args,
        headers: { ...args.headers, authorization: `Bearer ${token}` },
      }
    : args;

export const api = initClient(adminContract, {
  baseUrl: apiBaseUrl,
  baseHeaders: {},
  api: async (args: ApiFetcherArgs) => {
    const response = await tsRestFetchApi(withToken(args, accessToken));
    // Retried exactly once, and only for a request that actually carried a
    // token; auth routes are exempt so a failed refresh cannot loop.
    if (response.status !== 401 || !accessToken || isAuthCall(args)) {
      return response;
    }
    const fresh = await refreshOnce();
    if (!fresh) return response;
    return tsRestFetchApi(withToken(args, fresh));
  },
});

/** Same-credential fetch for the off-contract byte routes (302 → presigned). */
export async function authorizedFetch(path: string, init: RequestInit): Promise<Response> {
  const url = `${apiBaseUrl}${path}`;
  const send = (token: string | null): Promise<Response> =>
    fetch(url, {
      ...init,
      headers: {
        ...init.headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    });

  const response = await send(accessToken);
  if (response.status !== 401 || !accessToken) return response;
  const fresh = await refreshOnce();
  return fresh ? send(fresh) : response;
}

/** Bytes via a 302 route. See the dashboard's copy for the ORB reasoning. */
export async function fetchAuthedBlob(path: string, type?: string): Promise<Blob> {
  const response = await authorizedFetch(path, { redirect: "follow" });
  if (!response.ok) throw new Error(`Couldn’t load that file (${response.status})`);
  if (!type) return response.blob();
  return new Blob([await response.arrayBuffer()], { type });
}

/** Resolve a 302 route to its storage URL without reading the bytes. */
export async function resolveAuthedUrl(path: string): Promise<string> {
  const response = await authorizedFetch(path, {
    redirect: "follow",
    cache: "no-store",
  });
  await response.body?.cancel();
  if (!response.ok) throw new Error(`Couldn’t load that file (${response.status})`);
  return response.url;
}

export { errorMessage } from "@gb/contracts";
